import { Spinner } from "@chakra-ui/react";
import { useEffect, useState, useRef } from "react";
import useSincronizacao from "~/fetchs/useSincronizacao";
import { RefreshIcon } from "~misc/icons";
import { db } from "~db/db";
import { CheckIcon } from "@chakra-ui/icons";
import { saveLocalStorage } from "~services/utils/SaveIndexBD";
import { useSyncContext } from "~hooks/useSyncContext";
import { isTablet } from "react-device-detect";
import ModalAction from "~components/Modal/ModalAction";
import useUsuario from "~/fetchs/useUsuario";
import useVendedor from "~/fetchs/useVendedor";

import "./style.scss";

const requestWakeLock = async (wakeLockRef) => {
  try {
    if ("wakeLock" in navigator && !wakeLockRef.current) {
      wakeLockRef.current = await navigator.wakeLock.request("screen");
    }
  } catch (err) {
    console.error("Erro ao solicitar Wake Lock:", err);
  }
};

const releaseWakeLock = async (wakeLockRef) => {
  try {
    if (wakeLockRef.current) {
      await wakeLockRef.current.release();
      wakeLockRef.current = null;
    }
  } catch (err) {
    console.error("Erro ao liberar Wake Lock:", err);
  }
};

const SyncRunningView = ({ processLastFetchTime, setDateTimeLastFetch }) => {
  const { loading, progressBar, enableButton, activeSincronismo } = useSincronizacao(true, processLastFetchTime);
  const { setTableSyncActive } = useSyncContext();
  const wakeLockRef = useRef(null);

  useEffect(() => {
    if (loading) {
      requestWakeLock(wakeLockRef);
    } else {
      releaseWakeLock(wakeLockRef);
    }

    return () => {
      releaseWakeLock(wakeLockRef);
    };
  }, [loading]);

  useEffect(() => {
    setTableSyncActive(loading);
    return () => setTableSyncActive(false);
  }, [loading, setTableSyncActive]);

  useEffect(() => {
    if (!loading) {
      const now = new Date();
      saveLocalStorage("last-fetch-datetime-new", now);
      globalThis.dispatchEvent(new Event("last-fetch-updated"));
      setDateTimeLastFetch(now);
    }
  }, [loading, setDateTimeLastFetch]);

  return (
    <>
      <button>
        {enableButton ? (
          loading ? (
            <span id="synchronize-button">
              <Spinner className="icon" size="xl" />
              Sincronizando...
            </span>
          ) : (
            <span id="synchronize-button">
              <RefreshIcon />
              Sincronizar dados
            </span>
          )
        ) : (
          <span id="synchronize-button" className="!bg-[#9ACA3C]">
            <CheckIcon className="icon !w-[10px] !h-[10px]" />
            Dados sincronizados
          </span>
        )}
      </button>

      {loading && (
        <div className="synchronize-loading-container" data-test-id="container-synchronization">
          <div className="content">
            <Spinner color="#034EA2" size={"xl"} thickness="4px" />

            <h2 className="title">Sincronizando dados...</h2>
            {isTablet && (
              <p className="text-red-600 font-semibold text-sm mb-2">
                NÃO desligue ou bloqueie a tela do tablet durante a sincronização
              </p>
            )}
            <div className="progress-bar">
              <div className="bar" style={{ width: `${progressBar}%` }}></div>
              <span className="desc" style={{ left: `${progressBar}%` }}>
                {progressBar}%
              </span>
            </div>

            {activeSincronismo && (
              <div className="text-[#034EA2] relative px-4 py-2 rounded mt-4 after:content-[''] after:block after:h-[2px] after:w-1/2 after:bg-[#034EA2] after:absolute after:bottom-0 after:left-1/4">
                {activeSincronismo}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const SynchronizeButtonComponent = ({ processLastFetchTime, setDateTimeLastFetch }) => {
  const [enabled, setEnabled] = useState(false);
  const [modalSyncAlert, setModalSyncAlert] = useState(false);

  const { usuario } = useUsuario();
  const { vendedorId } = useVendedor();

  const cacheClear = async () => {
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      for (const cacheName of cacheNames) {
        await caches.delete(cacheName);
      }
    }
    localStorage.setItem("shouldSyncronize", "true");
    window.location.reload({ reload: true });
  };

  const handleClick = async () => {
    if (usuario?.slugAcessos?.includes("sincronismo-por-vendedor") && !vendedorId) {
      setModalSyncAlert(true);
      return;
    }

    await cacheClear();
  };

  const handleClickAlert = async () => {
    await cacheClear();
  };

  useEffect(() => {
    if (localStorage.getItem("shouldSyncronize") === "true") {
      db.token.clear();
      setEnabled(true);
      localStorage.removeItem("shouldSyncronize");
    }
  }, []);

  return (
    <>
      {enabled ? (
        <SyncRunningView
          processLastFetchTime={processLastFetchTime}
          setDateTimeLastFetch={setDateTimeLastFetch}
        />
      ) : (
        <button onClick={handleClick} data-test-id="button-synchronize">
          <span id="synchronize-button">
            <RefreshIcon />
            Sincronizar dados
          </span>
        </button>
      )}

      {modalSyncAlert && (
        <ModalAction
          isOpen={modalSyncAlert}
          closeModal={(e) => {
            setModalSyncAlert(false);
          }}
          title="Sincronização Geral de Dados"
          sub="Você está prestes a iniciar uma sincronização geral de dados. Isso pode afetar o desempenho do sistema. Deseja continuar?"
          buttonOne="Ok"
          linkOne={(e) => handleClickAlert(e)}
          buttonTwo="Cancelar"
          linkTwo={() => setModalSyncAlert(false)}
        />
      )}
    </>
  );
};

export default SynchronizeButtonComponent;
